/**
 * CentralHoshinPage.tsx
 * Hoshin Kanri — Central de Desdobramento Estratégico
 *
 * Árvore: Director (Objetivo) → Manager (Meta Tática) → Coordinator/Operator (Ação)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronRight,
  Target,
  TrendingUp,
  Plus,
  X,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Layers,
  Building2,
  BarChart2,
  History,
  Settings2,
  Activity,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useDataScope } from '../hooks/useDataScope';
import { getDepartments } from '../services/organizationStructureService';
import {
  getStrategicTree,
  getGoalActions,
  createStrategicObjective,
  updateObjectiveStatus,
  updateGoalProgress,
  type ObjectiveNode,
  type TacticalGoalNode,
  type ObjectiveStatus,
  type TacticalGoalStatus,
  type CreateObjectiveParams,
} from '../services/hoshinService';
import type { RoleType } from '../services/scopeService';

// ─── Constants ───────────────────────────────────────────────────────────────

const OBJECTIVE_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  active: 'Ativo',
  achieved: 'Alcançado',
  canceled: 'Cancelado',
};

const OBJECTIVE_STATUS_COLOR: Record<ObjectiveStatus, string> = {
  active: 'bg-blue-100 text-blue-700',
  achieved: 'bg-emerald-100 text-emerald-700',
  canceled: 'bg-slate-100 text-slate-500',
};

const GOAL_STATUS_LABEL: Record<TacticalGoalStatus, string> = {
  on_track: 'No Prazo',
  at_risk: 'Em Risco',
  delayed: 'Atrasado',
  completed: 'Concluído',
};

const GOAL_STATUS_COLOR: Record<TacticalGoalStatus, string> = {
  on_track: 'bg-emerald-100 text-emerald-700',
  at_risk: 'bg-amber-100 text-amber-700',
  delayed: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
};

const ACTION_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em Andamento',
  completed: 'Concluída',
  blocked: 'Bloqueada',
  cancelled: 'Cancelada',
};

const ACTION_STATUS_COLOR: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-500',
  in_progress: 'bg-blue-100 text-blue-600',
  completed: 'bg-emerald-100 text-emerald-700',
  blocked: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-400',
};

// ─── Sub-types ───────────────────────────────────────────────────────────────

interface GoalAction {
  id: string;
  title: string;
  status: string;
}

type PanelTarget =
  | { kind: 'objective'; data: ObjectiveNode }
  | { kind: 'goal'; data: TacticalGoalNode };

// ─── Create Objective Modal ── Enterprise Edition ──────────────────────────────

const KPI_OPTIONS = [
  { value: 'oee',       label: 'OEE — Eficiência Global de Equipamentos' },
  { value: 'lead_time', label: 'Lead Time (dias)' },
  { value: 'vat',       label: 'VAT — Value-Added Time (%)' },
  { value: 'cycle_eff', label: 'Eficiência de Ciclo (%)' },
  { value: 'fpq',       label: 'FPQ — First-Pass Quality (%)' },
  { value: 'takt',      label: 'Takt Time (min/peça)' },
  { value: 'other',     label: 'Outro KPI Customizado' },
] as const;
type KpiValue = (typeof KPI_OPTIONS)[number]['value'];

/** Converte ano + trimestre em ISO date do último dia do trimestre */
function quarterToDate(year: number, q: 1 | 2 | 3 | 4): string {
  const ends = ['03-31', '06-30', '09-30', '12-31'];
  return `${year}-${ends[q - 1]}`;
}

const CURRENT_YEAR = new Date().getFullYear();
const FISCAL_YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

interface CreateObjectiveModalProps {
  open: boolean;
  organizationId: string;
  userId: string;
  roleType: RoleType;
  onClose: () => void;
  onCreated: () => void;
}

function CreateObjectiveModal({
  open,
  organizationId,
  userId,
  roleType,
  onClose,
  onCreated,
}: CreateObjectiveModalProps) {
  const [title, setTitle]             = React.useState('');
  const [description, setDescription] = React.useState('');
  const [kpi, setKpi]                 = React.useState<KpiValue>('oee');
  const [targetValue, setTargetValue] = React.useState('');
  const [fiscalYear, setFiscalYear]   = React.useState<number>(CURRENT_YEAR);
  const [quarter, setQuarter]         = React.useState<1 | 2 | 3 | 4>(4);
  const [saving, setSaving]           = React.useState(false);
  const [error, setError]             = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setTitle(''); setDescription(''); setKpi('oee');
      setTargetValue(''); setFiscalYear(CURRENT_YEAR); setQuarter(4);
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!title.trim()) { setError('Defina o Norte Verdadeiro — o título é obrigatório.'); return; }

    setSaving(true);
    setError(null);
    try {
      // Encode KPI metadata into description so no schema change is needed
      const kpiLabel = KPI_OPTIONS.find(k => k.value === kpi)?.label ?? kpi;
      const descParts: string[] = [];
      if (description.trim()) descParts.push(description.trim());
      if (targetValue.trim()) descParts.push(`KPI: ${kpiLabel} | Alvo: ${targetValue.trim()}`);

      const params: CreateObjectiveParams = {
        organization_id: organizationId,
        created_by:      userId,
        title:           title.trim(),
        description:     descParts.length > 0 ? descParts.join('\n') : undefined,
        target_date:     quarterToDate(fiscalYear, quarter),
      };
      await createStrategicObjective(params, roleType);
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao criar objetivo.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative z-10 w-full max-w-2xl bg-white border border-slate-200 shadow-2xl flex flex-col rounded-sm">

        {/* ── Topbar ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2.5">
            <Target size={14} className="text-slate-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
              Novo Objetivo Estratégico
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
            <X size={15} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto max-h-[72vh]">

          {/* SEÇÃO A — Norte Verdadeiro */}
          <section>
            <SectionLabel letter="A" title="Norte Verdadeiro" />
            <div className="mt-3 space-y-3">
              <div>
                <FieldLabel required>Título do Objetivo</FieldLabel>
                <input
                  autoFocus
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Ex: Elevar o OEE Global da Planta para 85% até o fechamento do exercício"
                  className="mt-1 w-full border border-slate-200 px-3 py-2 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-500 transition-colors rounded-sm"
                />
              </div>
              <div>
                <FieldLabel>Declaração de Impacto Estratégico</FieldLabel>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Descreva o propósito sistêmico deste objetivo dentro do fluxo de valor. Ex: Redução de Muda no tempo de troca (SMED) como alavanca primária para elevar o OEE sem investimento adicional em capacidade."
                  rows={3}
                  className="mt-1 w-full border border-slate-200 px-3 py-2 text-[12px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-500 transition-colors resize-none rounded-sm leading-relaxed"
                />
              </div>
            </div>
          </section>

          {/* SEÇÃO B — Métrica de Impacto */}
          <section>
            <SectionLabel letter="B" title="Métrica de Impacto" />
            <div className="mt-3 grid grid-cols-5 gap-3">
              <div className="col-span-3">
                <FieldLabel required>KPI Associado</FieldLabel>
                <select
                  value={kpi}
                  onChange={e => setKpi(e.target.value as KpiValue)}
                  className="mt-1 w-full border border-slate-200 px-2.5 py-2 text-[12px] text-slate-700 focus:outline-none focus:border-slate-500 transition-colors rounded-sm bg-white"
                >
                  {KPI_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <FieldLabel required>Valor Alvo</FieldLabel>
                <input
                  type="text"
                  value={targetValue}
                  onChange={e => setTargetValue(e.target.value)}
                  placeholder="Ex: 85% ou 3,2 dias"
                  className="mt-1 w-full border border-slate-200 px-3 py-2 text-[12px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-500 transition-colors rounded-sm"
                />
              </div>
            </div>
          </section>

          {/* SEÇÃO C — Horizonte Temporal */}
          <section>
            <SectionLabel letter="C" title="Horizonte Temporal" />
            <div className="mt-3 grid grid-cols-5 gap-3 items-end">
              <div className="col-span-2">
                <FieldLabel required>Ano Fiscal</FieldLabel>
                <select
                  value={fiscalYear}
                  onChange={e => setFiscalYear(Number(e.target.value))}
                  className="mt-1 w-full border border-slate-200 px-2.5 py-2 text-[12px] text-slate-700 focus:outline-none focus:border-slate-500 transition-colors rounded-sm bg-white"
                >
                  {FISCAL_YEARS.map(y => (
                    <option key={y} value={y}>FY {y}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <FieldLabel required>Trimestre de Encerramento</FieldLabel>
                <div className="mt-1 flex gap-1.5">
                  {([1, 2, 3, 4] as const).map(q => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setQuarter(q)}
                      className={`flex-1 py-2 text-[11px] font-semibold rounded-sm border transition-colors ${
                        quarter === q
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800'
                      }`}
                    >
                      Q{q}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] text-slate-400 tabular-nums">
                  Data-alvo: {new Date(quarterToDate(fiscalYear, quarter)).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
          </section>

          {error && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">
              <AlertTriangle size={12} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <p className="text-[10px] text-slate-400">
            Premissa TPS: o objetivo deve ser mensurável, sistêmico e orientado à eliminação de Muda.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="text-[11px] px-4 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-sm transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-[11px] px-5 py-1.5 bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 rounded-sm transition-colors font-medium"
            >
              {saving ? 'Registrando…' : 'Registrar Objetivo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

/** Visual section label with letter badge */
function SectionLabel({ letter, title }: { letter: string; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex-shrink-0 w-5 h-5 rounded-sm bg-slate-800 text-white text-[9px] font-bold flex items-center justify-center">
        {letter}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{title}</span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
      {children}{required && <span className="ml-0.5 text-red-500 normal-case tracking-normal">*</span>}
    </p>
  );
}

function SkeletonLine({ w = 'w-full', h = 'h-3' }: { w?: string; h?: string }) {
  return <div className={`${w} ${h} rounded bg-slate-200 animate-pulse`} />;
}

function ObjectiveSkeleton() {
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-3">
        <SkeletonLine w="w-5" h="h-5" />
        <SkeletonLine w="w-64" />
        <SkeletonLine w="w-16" h="h-4" />
      </div>
      <SkeletonLine w="w-40" h="h-2.5" />
    </div>
  );
}

// ─── Action Badge ─────────────────────────────────────────────────────────────

function ActionStatusBadge({ status }: { status: string }) {
  const color = ACTION_STATUS_COLOR[status] ?? 'bg-slate-100 text-slate-500';
  const label = ACTION_STATUS_LABEL[status] ?? status;
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${color}`}>
      {label}
    </span>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ value }: { value: number }) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-emerald-500 rounded-full transition-all duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function UserAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      title={name}
      className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[9px] font-semibold flex items-center justify-center"
    >
      {initials}
    </div>
  );
}

// ─── Goal Row ─────────────────────────────────────────────────────────────────

interface GoalRowProps {
  goal: TacticalGoalNode;
  departmentName: string;
  canEdit: boolean;
  onEdit: (goal: TacticalGoalNode) => void;
}

function GoalRow({ goal, departmentName, canEdit, onEdit }: GoalRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [actions, setActions] = useState<GoalAction[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);

  const toggleActions = useCallback(async () => {
    if (!expanded && actions.length === 0) {
      setLoadingActions(true);
      try {
        const data = await getGoalActions(goal.id);
        setActions(data);
      } catch {
        // silently ignore
      } finally {
        setLoadingActions(false);
      }
    }
    setExpanded(prev => !prev);
  }, [expanded, actions.length, goal.id]);

  return (
    <div className="border border-slate-100 rounded-md bg-white">
      {/* Goal header */}
      <div className="flex items-start gap-3 p-3">
        {/* Expand toggle */}
        <button
          onClick={toggleActions}
          className="mt-0.5 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          aria-label="Expandir ações"
        >
          <ChevronRight
            size={14}
            className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          />
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-medium text-slate-800 leading-snug">{goal.title}</span>
            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${GOAL_STATUS_COLOR[goal.status]}`}>
              {GOAL_STATUS_LABEL[goal.status]}
            </span>
          </div>

          {/* Dept + assignee */}
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <Building2 size={10} className="flex-shrink-0" />
            <span>{departmentName}</span>
            <span className="text-slate-300">·</span>
            <UserAvatar name={goal.assigned_to} />
            <span className="truncate max-w-[120px]">{goal.assigned_to}</span>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2">
            <ProgressBar value={goal.progress} />
            <span className="flex-shrink-0 text-[11px] text-slate-500 font-medium tabular-nums">
              {goal.progress}%
            </span>
          </div>

          {/* Action counts */}
          <div className="text-[11px] text-slate-400">
            {goal.completed_action_count}/{goal.action_count} ações concluídas
          </div>
        </div>

        {/* Edit button */}
        {canEdit && (
          <button
            onClick={() => onEdit(goal)}
            className="flex-shrink-0 text-[11px] text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded px-2 py-0.5 transition-colors"
          >
            Editar
          </button>
        )}
      </div>

      {/* Actions list */}
      {expanded && (
        <div className="border-t border-slate-100 px-8 py-2 space-y-1.5">
          {loadingActions && (
            <div className="space-y-1.5 py-1">
              <SkeletonLine w="w-full" h="h-2.5" />
              <SkeletonLine w="w-3/4" h="h-2.5" />
            </div>
          )}
          {!loadingActions && actions.length === 0 && (
            <p className="text-[11px] text-slate-400 italic py-1">Nenhuma ação vinculada.</p>
          )}
          {!loadingActions && actions.map(action => (
            <div key={action.id} className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-600 truncate">{action.title}</span>
              <ActionStatusBadge status={action.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Objective Card ───────────────────────────────────────────────────────────

interface ObjectiveCardProps {
  objective: ObjectiveNode;
  departmentMap: Map<string, string>;
  canEditObjective: boolean;
  canEditGoal: boolean;
  onEditObjective: (obj: ObjectiveNode) => void;
  onEditGoal: (goal: TacticalGoalNode) => void;
}

function ObjectiveCard({
  objective,
  departmentMap,
  canEditObjective,
  canEditGoal,
  onEditObjective,
  onEditGoal,
}: ObjectiveCardProps) {
  const [expanded, setExpanded] = useState(true);

  const totalGoals = objective.goals.length;
  const completedGoals = objective.goals.filter(g => g.status === 'completed').length;
  const targetDate = new Date(objective.target_date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      {/* Objective header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(prev => !prev)}
      >
        <Target size={15} className="flex-shrink-0 text-slate-400" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-slate-800">{objective.title}</span>
            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${OBJECTIVE_STATUS_COLOR[objective.status]}`}>
              {OBJECTIVE_STATUS_LABEL[objective.status]}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Meta: {targetDate}</span>
            {totalGoals > 0 && (
              <>
                <span className="text-slate-200">·</span>
                <span>{completedGoals}/{totalGoals} metas concluídas</span>
              </>
            )}
          </div>
        </div>

        {canEditObjective && (
          <button
            onClick={e => { e.stopPropagation(); onEditObjective(objective); }}
            className="flex-shrink-0 text-[11px] text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded px-2 py-0.5 transition-colors"
          >
            Editar
          </button>
        )}

        <ChevronRight
          size={14}
          className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {/* Goals list */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-2">
          {objective.goals.length === 0 && (
            <p className="text-[12px] text-slate-400 italic">Nenhuma meta tática desdobrada.</p>
          )}
          {objective.goals.map(goal => (
            <GoalRow
              key={goal.id}
              goal={goal}
              departmentName={departmentMap.get(goal.department_id) ?? goal.department_id}
              canEdit={canEditGoal}
              onEdit={onEditGoal}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Edit Panel — Cockpit do Objetivo ────────────────────────────────────────

type CockpitTab = 'config' | 'bowling' | 'catchball';

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface EditPanelProps {
  target: PanelTarget | null;
  roleType: RoleType;
  onClose: () => void;
  onSaved: () => void;
}

function EditPanel({ target, roleType, onClose, onSaved }: EditPanelProps) {
  const [activeTab, setActiveTab] = useState<CockpitTab>('config');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [objStatus, setObjStatus] = useState<ObjectiveStatus>('active');
  const [goalProgress, setGoalProgress] = useState(0);
  const [goalStatus, setGoalStatus]     = useState<TacticalGoalStatus>('on_track');

  useEffect(() => {
    if (!target) return;
    setActiveTab('config');
    setError(null);
    if (target.kind === 'objective') {
      setObjStatus(target.data.status);
    } else {
      setGoalProgress(target.data.progress);
      setGoalStatus(target.data.status);
    }
  }, [target]);

  const handleSave = async () => {
    if (!target) return;
    setSaving(true);
    setError(null);
    try {
      if (target.kind === 'objective') {
        await updateObjectiveStatus(target.data.id, objStatus, roleType);
      } else {
        await updateGoalProgress(target.data.id, goalProgress, goalStatus, roleType);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const isVisible = target !== null;

  // Bowling: derive months between creation and target_date
  const bowlingMonths = React.useMemo(() => {
    if (!target) return [];
    const createdAt = target.kind === 'objective'
      ? new Date(target.data.created_at)
      : new Date(target.data.created_at);
    const targetDt = target.kind === 'objective'
      ? new Date(target.data.target_date)
      : new Date(); // goals don't have target_date — use today as proxy
    const start = new Date(createdAt.getFullYear(), createdAt.getMonth(), 1);
    const end   = new Date(targetDt.getFullYear(), targetDt.getMonth(), 1);
    const now   = new Date();
    const months: { label: string; isPast: boolean; isCurrent: boolean }[] = [];
    const cur = new Date(start);
    while (cur <= end && months.length < 12) {
      const m = cur.getMonth();
      const y = cur.getFullYear();
      const d = new Date(y, m, 1);
      const isPast    = d < new Date(now.getFullYear(), now.getMonth(), 1);
      const isCurrent = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      months.push({ label: `${MONTHS_SHORT[m]} ${y !== now.getFullYear() ? y : ''}`.trim(), isPast, isCurrent });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }, [target]);

  // Catchball: derive a simple activity feed from available timestamps
  const catchballFeed = React.useMemo(() => {
    if (!target) return [];
    const items: { date: string; actor: string; text: string }[] = [];
    const data = target.data;
    if ('created_at' in data) {
      items.push({
        date: new Date(data.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
        actor: target.kind === 'objective' ? 'Diretor' : 'Gerente',
        text: target.kind === 'objective'
          ? `Objetivo registrado: "${data.title}"`
          : `Meta tática desdobrada: "${data.title}"`,
      });
    }
    if ('updated_at' in data && data.updated_at !== data.created_at) {
      items.push({
        date: new Date(data.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
        actor: target.kind === 'objective' ? 'Diretor' : 'Gerente',
        text: target.kind === 'objective'
          ? `Status atualizado para "${OBJECTIVE_STATUS_LABEL[data.status as ObjectiveStatus]}"`
          : `Progresso atualizado: ${(data as TacticalGoalNode).progress}% — ${GOAL_STATUS_LABEL[data.status as TacticalGoalStatus]}`,
      });
    }
    return items.reverse();
  }, [target]);

  // Progress estimate for bowling: from 0% at start to current progress at now
  const progressAtMonth = (monthIdx: number, totalMonths: number, currentProgress: number) => {
    if (totalMonths === 0) return 0;
    return Math.round((monthIdx / Math.max(1, totalMonths - 1)) * currentProgress);
  };

  const tabItems: { id: CockpitTab; label: string; Icon: React.ElementType }[] = [
    { id: 'config',   label: 'Configurações',      Icon: Settings2  },
    { id: 'bowling',  label: 'Bowling Chart',       Icon: BarChart2  },
    { id: 'catchball',label: 'Catchball',           Icon: History    },
  ];

  return (
    <>
      {isVisible && <div className="fixed inset-0 z-40 bg-black/10" onClick={onClose} />}

      <div
        className={`fixed top-0 right-0 h-full w-[480px] z-50 bg-white border-l border-slate-200 flex flex-col transition-transform duration-300 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-5 py-3.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              {target?.kind === 'objective'
                ? <Target size={14} className="text-slate-500 flex-shrink-0" />
                : <TrendingUp size={14} className="text-slate-500 flex-shrink-0" />}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  {target?.kind === 'objective' ? 'Cockpit do Objetivo' : 'Cockpit da Meta Tática'}
                </p>
                <p className="text-[12px] font-semibold text-slate-800 leading-snug mt-0.5 max-w-[340px] truncate">
                  {target?.data.title ?? ''}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0 mt-0.5">
              <X size={15} />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex gap-0 mt-3 border-b border-slate-200 -mb-px">
            {tabItems.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-slate-800 text-slate-800'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                <Icon size={11} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* TAB: Configurações */}
          {activeTab === 'config' && (
            <div className="px-5 py-4 space-y-5">
              {target?.kind === 'objective' && (
                <>
                  {target.data.description && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                        Declaração Estratégica
                      </p>
                      <p className="text-[12px] text-slate-600 leading-relaxed">{target.data.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                      Status do Objetivo
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {(['active', 'achieved', 'canceled'] as ObjectiveStatus[]).map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setObjStatus(s)}
                          className={`py-2 text-[11px] font-medium rounded-sm border transition-colors ${
                            objStatus === s
                              ? s === 'achieved'
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : s === 'canceled'
                                  ? 'bg-slate-500 text-white border-slate-500'
                                  : 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          {OBJECTIVE_STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <Calendar size={11} className="flex-shrink-0" />
                    <span>Data-alvo:{' '}
                      <span className="font-semibold text-slate-700">
                        {new Date(target.data.target_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </span>
                    </span>
                  </div>
                </>
              )}

              {target?.kind === 'goal' && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Progresso</p>
                      <span className="text-[13px] font-bold text-emerald-600 tabular-nums">{goalProgress}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={goalProgress}
                      onChange={e => setGoalProgress(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                    <ProgressBar value={goalProgress} />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Status</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(['on_track', 'at_risk', 'delayed', 'completed'] as TacticalGoalStatus[]).map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setGoalStatus(s)}
                          className={`py-2 text-[11px] font-medium rounded-sm border transition-colors ${
                            goalStatus === s
                              ? s === 'completed'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : s === 'on_track'
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : s === 'at_risk'
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          {GOAL_STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">
                  <AlertTriangle size={11} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* TAB: Bowling Chart */}
          {activeTab === 'bowling' && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                Planejado vs Realizado — Ciclo Mensal
              </p>
              {bowlingMonths.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">Período não calculável.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px] border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left pr-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                          Indicador
                        </th>
                        {bowlingMonths.map((m, i) => (
                          <th
                            key={i}
                            className={`text-center px-1 py-1.5 font-semibold whitespace-nowrap ${
                              m.isCurrent ? 'text-slate-800' : 'text-slate-400'
                            }`}
                          >
                            {m.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {/* Planejado row */}
                      <tr>
                        <td className="pr-3 py-2 text-slate-500 font-medium whitespace-nowrap">Planejado</td>
                        {bowlingMonths.map((m, i) => {
                          const planned = progressAtMonth(i, bowlingMonths.length, 100);
                          return (
                            <td key={i} className="text-center px-1 py-2">
                              <span className={`tabular-nums font-mono text-[10px] ${m.isCurrent ? 'font-bold text-slate-700' : 'text-slate-400'}`}>
                                {planned}%
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                      {/* Realizado row */}
                      <tr>
                        <td className="pr-3 py-2 text-slate-500 font-medium whitespace-nowrap">Realizado</td>
                        {bowlingMonths.map((m, i) => {
                          const current = target?.kind === 'goal' ? goalProgress : (target?.kind === 'objective' ? (target.data.status === 'achieved' ? 100 : 0) : 0);
                          const planned = progressAtMonth(i, bowlingMonths.length, 100);
                          const actual  = m.isPast || m.isCurrent
                            ? progressAtMonth(i, bowlingMonths.length, current)
                            : null;
                          const hit = actual !== null && actual >= planned;
                          return (
                            <td key={i} className="text-center px-1 py-2">
                              {actual !== null ? (
                                <span
                                  className={`inline-block min-w-[28px] rounded-sm px-1 py-0.5 tabular-nums font-mono text-[10px] font-semibold ${
                                    hit
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {actual}%
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-3 text-[9px] text-slate-400">
                Verde: atingiu ou superou o planejado · Vermelho: abaixo do plano · — : período futuro
              </p>
            </div>
          )}

          {/* TAB: Catchball / Histórico */}
          {activeTab === 'catchball' && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                Histórico de Desdobramentos e Catchball
              </p>
              {catchballFeed.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">Nenhum registro de atividade disponível.</p>
              ) : (
                <div className="relative pl-4 border-l border-slate-200 space-y-4">
                  {catchballFeed.map((item, i) => (
                    <div key={i} className="relative">
                      <span className="absolute -left-[17px] top-1 w-2 h-2 rounded-full bg-slate-400 border-2 border-white" />
                      <p className="text-[10px] text-slate-400 tabular-nums">{item.date}</p>
                      <p className="text-[11px] font-medium text-slate-700 mt-0.5">{item.text}</p>
                      <p className="text-[10px] text-slate-400">por {item.actor}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                  Registrar Nota de Catchball
                </p>
                <textarea
                  rows={2}
                  placeholder="Descreva um ajuste de escopo, validação ou decisão de desdobramento…"
                  className="w-full border border-slate-200 px-3 py-2 text-[11px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-500 transition-colors resize-none rounded-sm"
                />
                <button className="mt-2 text-[10px] px-3 py-1.5 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 rounded-sm transition-colors font-medium flex items-center gap-1">
                  <Activity size={10} />
                  Registrar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer (only shown on config tab) ─────────────────────────── */}
        {activeTab === 'config' && (
          <div className="flex-shrink-0 px-5 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="text-[11px] px-4 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-sm transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-[11px] px-5 py-1.5 bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 rounded-sm transition-colors font-medium"
            >
              {saving ? 'Salvando…' : 'Salvar Alterações'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const CentralHoshinPage: React.FC = () => {
  const { user } = useAuth();
  const organizationId = useOrganizationId();
  const { roleType } = useDataScope();

  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState<ObjectiveNode[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [departmentMap, setDepartmentMap] = useState<Map<string, string>>(new Map());
  const [panelTarget, setPanelTarget] = useState<PanelTarget | null>(null);
  const [createObjectiveOpen, setCreateObjectiveOpen] = useState(false);

  const canEditObjective = roleType === 'director';
  const canEditGoal = roleType === 'manager' || roleType === 'director';

  const loadTree = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const [tree, depts] = await Promise.all([
        getStrategicTree(organizationId),
        getDepartments(organizationId),
      ]);
      setObjectives(tree);
      setDepartmentMap(new Map(depts.map(d => [d.id, d.name])));
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Erro ao carregar a árvore Hoshin.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // Summary stats
  const totalObjectives = objectives.length;
  const achievedObjectives = objectives.filter(o => o.status === 'achieved').length;
  const totalGoals = objectives.reduce((acc, o) => acc + o.goals.length, 0);
  const atRiskGoals = objectives.reduce(
    (acc, o) => acc + o.goals.filter(g => g.status === 'at_risk' || g.status === 'delayed').length,
    0,
  );

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-slate-500" />
            <h1 className="text-[15px] font-semibold text-slate-800">Hoshin Kanri</h1>
            <span className="text-[11px] text-slate-400 ml-1">Desdobramento Estratégico</span>
          </div>

          {canEditObjective && (
            <button
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-700 transition-colors"
              onClick={() => setCreateObjectiveOpen(true)}
            >
              <Plus size={13} />
              Novo Objetivo
            </button>
          )}
        </div>

        {/* Summary ribbon */}
        {!loading && (
          <div className="mt-3 flex items-center gap-4">
            <SummaryStat
              icon={<Target size={12} />}
              label="Objetivos"
              value={totalObjectives}
              highlight={false}
            />
            <SummaryStat
              icon={<CheckCircle2 size={12} />}
              label="Alcançados"
              value={achievedObjectives}
              highlight={achievedObjectives > 0}
              colorClass="text-emerald-600"
            />
            <SummaryStat
              icon={<TrendingUp size={12} />}
              label="Metas Táticas"
              value={totalGoals}
              highlight={false}
            />
            {atRiskGoals > 0 && (
              <SummaryStat
                icon={<AlertTriangle size={12} />}
                label="Em Risco / Atrasadas"
                value={atRiskGoals}
                highlight
                colorClass="text-amber-600"
              />
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <ObjectiveSkeleton key={i} />)}
          </div>
        )}

        {/* Error state */}
        {!loading && fetchError && (
          <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-100 px-5 py-4">
            <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-red-700">Falha ao carregar a árvore estratégica</p>
              <p className="text-[12px] text-red-600 mt-0.5">{fetchError}</p>
              <button
                onClick={loadTree}
                className="mt-2 text-[11px] text-red-600 underline hover:no-underline"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !fetchError && objectives.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Target size={32} className="text-slate-300 mb-3" />
            <p className="text-[14px] font-medium text-slate-600">Nenhum objetivo estratégico cadastrado</p>
            <p className="text-[12px] text-slate-400 mt-1">
              {canEditObjective
                ? 'Clique em "Novo Objetivo" para iniciar o desdobramento.'
                : 'Aguarde que um Diretor cadastre os objetivos estratégicos da organização.'}
            </p>
          </div>
        )}

        {/* Tree */}
        {!loading && !fetchError && objectives.length > 0 && (
          <div className="space-y-3">
            {objectives.map(obj => (
              <ObjectiveCard
                key={obj.id}
                objective={obj}
                departmentMap={departmentMap}
                canEditObjective={canEditObjective}
                canEditGoal={canEditGoal}
                onEditObjective={data => setPanelTarget({ kind: 'objective', data })}
                onEditGoal={data => setPanelTarget({ kind: 'goal', data })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit panel */}
      <EditPanel
        target={panelTarget}
        roleType={roleType}
        onClose={() => setPanelTarget(null)}
        onSaved={loadTree}
      />

      {/* Create objective modal */}
      <CreateObjectiveModal
        open={createObjectiveOpen}
        organizationId={organizationId}
        userId={user?.id ?? ''}
        roleType={roleType}
        onClose={() => setCreateObjectiveOpen(false)}
        onCreated={loadTree}
      />
    </div>
  );
};

// ─── Summary stat pill ────────────────────────────────────────────────────────

interface SummaryStatProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight: boolean;
  colorClass?: string;
}

function SummaryStat({ icon, label, value, highlight, colorClass = 'text-slate-700' }: SummaryStatProps) {
  return (
    <div className={`flex items-center gap-1.5 text-[11px] ${highlight ? colorClass : 'text-slate-500'}`}>
      <span className={highlight ? colorClass : 'text-slate-400'}>{icon}</span>
      <span className={`font-semibold tabular-nums ${highlight ? colorClass : 'text-slate-700'}`}>{value}</span>
      <span>{label}</span>
    </div>
  );
}

export default CentralHoshinPage;
